import React from "react";
import { useDispatch } from "react-redux";
import { setBuyInput, setSellInput } from "src/store/features/swap.slice";
export interface IRefLogic {
  sellInputRef: React.MutableRefObject<HTMLInputElement | null>;
  buyInputRef: React.MutableRefObject<HTMLInputElement | null>;
  currentInputRef: React.MutableRefObject<HTMLInputElement | null>;
  previousSellInputRef: React.MutableRefObject<{ units: string; code: string }>;
  previousBuyInputRef: React.MutableRefObject<{ units: string; code: string }>;
}

/**
 * Custom React hook for managing references and logic related to trade inputs.
 * Expand upon this hook when you want to include more functionality related to trade inputs.
 *
 * @returns {object} An object containing:
 * - `ref`: An object holding various input refs including:
 *   - `sellInputRef`: Reference to the sell input field.
 *   - `buyInputRef`: Reference to the buy input field.
 *   - `currentInputRef`: Reference to the currently focused input field.
 *   - `previousSellInputRef`: Reference holding the previous value and code of the sell input.
 *   - `previousBuyInputRef`: Reference holding the previous value and code of the buy input.
 * - `handleSellInputKeyDown`: Function to handle the "Enter" key event in the sell input, moving focus to the buy input.
 * - `handleBuyInputKeyDown`: Function to handle the "Enter" key event in the buy input, blurring the input.
 * - `handleInputFocus`: Function to handle the focus event on an input, setting the current input reference.
 * - `handleInputBlur`: Function to handle the blur event on an input, resetting the input value to "0" if left empty.
 * - `resetInputRefs`: Function to reset the sell and buy input refs to `null`.
 */
const useTradeRefLogic = () => {
  const dispatch = useDispatch();
  // sell and buy input ref
  const sellInputRef = React.useRef<HTMLInputElement | null>(null);
  const buyInputRef = React.useRef<HTMLInputElement | null>(null);

  const currentInputRef = React.useRef<HTMLInputElement | null>(null);

  const previousSellInputRef = React.useRef({ units: "0", code: "Select" });
  const previousBuyInputRef = React.useRef({ units: "0", code: "Select" });

  /**
   * Sets the `currentInputRef` to the currently focused input element.
   */
  const handleInputFocus = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    currentInputRef.current = event.target as HTMLInputElement;
  };

  /**
   * Handles the blur event on the sell or buy input fields.
   * Resets the input value to "0" and dispatches an action to update the state if the input is left empty.
   */
  const handleInputBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    if (event.target.value === "" && event.target.id === "sellInput") {
      dispatch(setSellInput({ value: "0", units: "0" }));
    }
    if (event.target.value === "" && event.target.id === "buyInput") {
      dispatch(setBuyInput({ value: "0", units: "0" }));
    }
  };

  /**
   * Handles the "Enter" key press event on the sell input field.
   * Moves the focus to the buy input field if it exists.
   */
  const handleSellInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter" && buyInputRef.current) buyInputRef.current.focus();
  };

  /**
   * Handles the "Enter" key press event on the buy input field.
   * Blurs the buy input field if it exists.
   */
  const handleBuyInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter" && buyInputRef.current) buyInputRef.current.blur();
  };

  /**
   * Resets the sell and buy input references to `null`.
   */
  const resetInputRefs = (): void => {
    sellInputRef.current = null;
    buyInputRef.current = null;
  };

  return {
    ref: {
      sellInputRef,
      buyInputRef,
      currentInputRef,
      previousSellInputRef,
      previousBuyInputRef,
    },
    handleSellInputKeyDown,
    handleBuyInputKeyDown,
    handleInputFocus,
    handleInputBlur,
    resetInputRefs,
  };
};

export default useTradeRefLogic;
